// src/pages/laboratory/LaboratoryNotifications.jsx - Complete Updated Version
import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Search,
  Check,
  CheckCheck,
  Trash2,
  RefreshCw,
  Calendar,
  Package,
  FileText,
  CreditCard,
  Tag,
  TrendingUp,
  MessageSquare,
  AlertCircle,
  Download,
  Eye,
  Users,
  Building2,
  Upload,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import notificationApi from '../../api/notification-api';
import { authApi } from '../../api/auth-api';

const LaboratoryNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [markingAsRead, setMarkingAsRead] = useState(new Set());
  const [deleting, setDeleting] = useState(new Set());
  const [downloadingContract, setDownloadingContract] = useState(new Set());
  const [uploadingContract, setUploadingContract] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const userData = authApi.getUserData();
      if (!userData?.id) {
        console.error('No user data found');
        return;
      }

      // For laboratory users, get notifications by user ID
      const data = await notificationApi.getUserNotifications(userData.id, 50, 0, filter);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading laboratory notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    setMarkingAsRead(prev => new Set([...prev, notificationId]));
    try {
      await notificationApi.markNotificationSeen(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, seen: true }
            : notif
        )
      );
      toast.success('Notification marquée comme lue');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setMarkingAsRead(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const userData = authApi.getUserData();
      if (!userData?.id) return;

      await notificationApi.markAllNotificationsSeen(userData.id);
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, seen: true }))
      );
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteNotification = async (notificationId) => {
    setDeleting(prev => new Set([...prev, notificationId]));
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      toast.success('Notification supprimée');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Handle contract download for laboratory interface
  const handleDownloadContract = async (notification) => {
    const notificationId = notification.id;
    setDownloadingContract(prev => new Set([...prev, notificationId]));
    
    try {
      // Extract contract information from notification message
      const contractInfo = extractContractInfo(notification.message);
      
      if (!contractInfo.contractNumber) {
        toast.error('Impossible d\'extraire les informations du contrat');
        return;
      }

      // Download the contract using fetch
      const response = await fetch(`/api/contracts/download/${contractInfo.contractNumber}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download contract');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat-${contractInfo.contractNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Contrat téléchargé avec succès');
      
      // Mark notification as read after successful download
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Erreur lors du téléchargement du contrat');
    } finally {
      setDownloadingContract(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  // Handle signed contract upload for laboratory interface
  const handleUploadSignedContract = async (notification) => {
    const notificationId = notification.id;
    
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.style.display = 'none';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (file.type !== 'application/pdf') {
        toast.error('Seuls les fichiers PDF sont acceptés');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 10MB');
        return;
      }

      setUploadingContract(prev => new Set([...prev, notificationId]));
      
      try {
        const contractInfo = extractContractInfo(notification.message);
        
        if (!contractInfo.contractNumber) {
          toast.error('Impossible d\'extraire les informations du contrat');
          return;
        }

        // Get user data for laboratory information
        const userData = authApi.getUserData();
        const laboratoryName = userData?.laboratory?.nom || 'Laboratoire';

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('signedContract', file);
        formData.append('contractNumber', contractInfo.contractNumber);
        formData.append('laboratoryName', laboratoryName);
        formData.append('promotions', contractInfo.promotions || 'Non spécifiées');

        // Upload signed contract
        const response = await fetch('/api/contracts/upload-signed', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to upload signed contract');
        }

        const result = await response.json();
        
        toast.success('Contrat signé envoyé avec succès');
        
        // Mark notification as read
        await markAsRead(notificationId);
        
      } catch (error) {
        console.error('Error uploading signed contract:', error);
        toast.error('Erreur lors de l\'envoi du contrat signé');
      } finally {
        setUploadingContract(prev => {
          const newSet = new Set(prev);
          newSet.delete(notificationId);
          return newSet;
        });
      }
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  // Helper function to extract contract info from notification message
  const extractContractInfo = (message) => {
    // Extract contract info from notification message
    // Format: "Nouveau contrat créé: ContractName (ContractNumber). Promotions associées: PromoNames."
    const contractMatch = message.match(/Nouveau contrat créé: (.+?) \((.+?)\)/);
    const promotionsMatch = message.match(/Promotions associées: (.+?)\./);
    
    return {
      laboratoryName: contractMatch ? contractMatch[1] : null,
      contractNumber: contractMatch ? contractMatch[2] : null,
      promotions: promotionsMatch ? promotionsMatch[1] : null
    };
  };

  const getNotificationIcon = (type) => {
    const iconProps = { size: 18, className: "text-gray-600" };
    switch (type) {
      case 'PRODUCT_APPROVED':
      case 'PRODUCT_REJECTED':
      case 'PRODUCT_PERFORMANCE':
        return <Package {...iconProps} />;
      case 'PAYMENT_RECEIVED':
      case 'PAYMENT_DENIED':
        return <CreditCard {...iconProps} />;
      case 'PROMOTION_EXPIRING':
      case 'PROMOTION_APPROVED':
        return <Tag {...iconProps} />;
      case 'CONTRACT_RENEWAL':
      case 'CONTRACT_EXPIRED':
      case 'CONTRACT_ALERT':
        return <FileText {...iconProps} />;
      case 'SALES_REPORT':
        return <TrendingUp {...iconProps} />;
      case 'MESSAGE_RECEIVED':
        return <MessageSquare {...iconProps} />;
      case 'SYSTEM_ALERT':
        return <AlertCircle {...iconProps} />;
      default:
        return <Bell {...iconProps} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'PRODUCT_APPROVED':
      case 'PAYMENT_RECEIVED':
      case 'PROMOTION_APPROVED':
        return 'border-l-green-500 bg-green-50';
      case 'PRODUCT_REJECTED':
      case 'PAYMENT_DENIED':
      case 'CONTRACT_EXPIRED':
        return 'border-l-red-500 bg-red-50';
      case 'PRODUCT_PERFORMANCE':
      case 'SALES_REPORT':
        return 'border-l-blue-500 bg-blue-50';
      case 'PROMOTION_EXPIRING':
      case 'CONTRACT_RENEWAL':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'CONTRACT_ALERT':
        return 'border-l-orange-500 bg-orange-50';
      case 'MESSAGE_RECEIVED':
        return 'border-l-purple-500 bg-purple-50';
      case 'SYSTEM_ALERT':
        return 'border-l-gray-500 bg-gray-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getActionButton = (notification) => {
    const { notificationType, message, id } = notification;
    
    // Contract notifications with download/sign/upload actions
    if (notificationType === 'CONTRACT_ALERT' && message.includes('Nouveau contrat créé')) {
      return (
        <div className="flex space-x-2 mt-3">
          <button 
            onClick={() => handleDownloadContract(notification)}
            disabled={downloadingContract.has(id)}
            className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={14} className="mr-2" />
            {downloadingContract.has(id) ? 'Téléchargement...' : 'Télécharger'}
          </button>
          <button 
            onClick={() => handleUploadSignedContract(notification)}
            disabled={uploadingContract.has(id)}
            className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload size={14} className="mr-2" />
            {uploadingContract.has(id) ? 'Envoi...' : 'Renvoyer signé'}
          </button>
        </div>
      );
    }

    // Contract renewal notifications
    if (notificationType === 'CONTRACT_RENEWAL') {
      return (
        <button 
          className="flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors mt-3"
        >
          <Eye size={14} className="mr-2" />
          Voir détails
        </button>
      );
    }

    // Product approval notifications
    if (notificationType === 'PRODUCT_APPROVED' || notificationType === 'PRODUCT_REJECTED') {
      return (
        <button 
          className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors mt-3"
        >
          <Package size={14} className="mr-2" />
          Voir produit
        </button>
      );
    }

    // Payment notifications
    if (notificationType === 'PAYMENT_RECEIVED' || notificationType === 'PAYMENT_DENIED') {
      return (
        <button 
          className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors mt-3"
        >
          <CreditCard size={14} className="mr-2" />
          Voir paiement
        </button>
      );
    }
    
    return null;
  };

  const formatDate = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffInMinutes = Math.floor((now - notifDate) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `Il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    }
  };

  const getFilterOptions = () => [
    { value: 'all', label: 'Toutes', icon: Bell },
    { value: 'unread', label: 'Non lues', icon: AlertCircle },
    { value: 'CONTRACT_ALERT', label: 'Contrats', icon: FileText },
    { value: 'PRODUCT_APPROVED', label: 'Produits', icon: Package },
    { value: 'PAYMENT_RECEIVED', label: 'Paiements', icon: CreditCard },
    { value: 'PROMOTION_APPROVED', label: 'Promotions', icon: Tag },
    { value: 'MESSAGE_RECEIVED', label: 'Messages', icon: MessageSquare },
  ];

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = searchTerm === '' || 
      notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.notificationType.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const unreadCount = notifications.filter(n => !n.seen).length;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Bell className="mr-3 text-blue-600" size={28} />
              Notifications
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez vos notifications et alertes importantes
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                showFilters 
                  ? 'bg-blue-600 text-white' 
                  : 'text-blue-600 bg-blue-100 hover:bg-blue-200'
              }`}
            >
              <Filter size={16} className="mr-2" />
              Filtres
            </button>
            
            <button
              onClick={loadNotifications}
              className="flex items-center px-4 py-2 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <RefreshCw size={16} className="mr-2" />
              Actualiser
            </button>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center px-4 py-2 text-green-600 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
              >
                <CheckCheck size={16} className="mr-2" />
                Tout marquer comme lu
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher dans les notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                {getFilterOptions().map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                      filter === value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={14} className="mr-1" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Close Filters */}
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Chargement des notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune notification</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Aucune notification ne correspond à votre recherche.' : 'Vous n\'avez aucune notification pour le moment.'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg shadow border-l-4 p-6 transition-all hover:shadow-md ${getNotificationColor(notification.notificationType)} ${!notification.seen ? 'ring-2 ring-blue-200' : ''}`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.notificationType)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <p className="text-sm font-medium text-gray-900">
                        {notification.notificationType.replace(/_/g, ' ')}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        notification.notificationType.includes('APPROVED') || notification.notificationType.includes('RECEIVED') ? 
                        'bg-green-100 text-green-800' :
                        notification.notificationType.includes('PAYMENT') ? 'bg-blue-100 text-blue-800' :
                        notification.notificationType.includes('PROMOTION') ? 'bg-orange-100 text-orange-800' :
                        notification.notificationType.includes('CONTRACT') ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {notification.notificationType.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(notification.createdAt)}
                      </span>
                      {!notification.seen && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-3">
                    {notification.message}
                  </p>
                  
                  {/* Action buttons */}
                  {getActionButton(notification)}
                  
                  {/* Mark as read / Delete buttons */}
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                    <div className="flex space-x-3">
                      {!notification.seen && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          disabled={markingAsRead.has(notification.id)}
                          className="flex items-center px-3 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                        >
                          <Check size={12} className="mr-1" />
                          {markingAsRead.has(notification.id) ? 'Marquage...' : 'Marquer comme lu'}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      disabled={deleting.has(notification.id)}
                      className="flex items-center px-3 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={12} className="mr-1" />
                      {deleting.has(notification.id) ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LaboratoryNotifications;